import React, { useState } from "react";
import Autocomplete from "react-google-autocomplete";
import Geocode from "react-geocode";
import { toast } from "react-toastify";

// Fetches detailed place information using Google Places API place_id
const getPlaceDetails = (placeId) => {
  return new Promise((resolve, reject) => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      reject(new Error('Google Maps API not loaded'));
      return;
    }
    
    const service = new window.google.maps.places.PlacesService(document.createElement('div'));
    service.getDetails(
      {
        placeId: placeId,
        fields: ['name', 'formatted_address', 'address_components', 'types']
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          resolve(place);
        } else {
          reject(new Error(`Places service error: ${status}`));
        }
      }
    );
  });
};

// LocationAutocomplete Component - Provides Google Places autocomplete input with "Use Current Location" button
const LocationAutocomplete = ({
  value,
  onChange,
  onPlaceSelected,
  placeholder = "*Location",
  className = "form-control",
  required = false,
  showCurrentLocationButton = true,
  locationType = "current"
}) => {
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  // Handles place selection from autocomplete dropdown - fetches place name and combines with formatted address
  const handlePlaceSelected = (place) => {
    let displayText = place.name || place.description || place.formatted_address || '';
    
    if (place.structured_formatting) {
      displayText = place.structured_formatting.main_text || 
                   place.structured_formatting.secondary_text || 
                   displayText;
    }
    
    if (place.address_components && !place.name) {
      const establishmentComponent = place.address_components.find(comp => 
        comp.types && comp.types.includes('establishment')
      );
      if (establishmentComponent) {
        displayText = establishmentComponent.long_name || displayText;
      } else {
        const poiComponent = place.address_components.find(comp => 
          comp.types && comp.types.includes('point_of_interest')
        );
        if (poiComponent) {
          displayText = poiComponent.long_name || displayText;
        }
      }
    }
    
    if (place.place_id && !place.name && window.google && window.google.maps && window.google.maps.places) {
      getPlaceDetails(place.place_id)
        .then((placeDetails) => {
          const name = placeDetails.name || '';
          const address = placeDetails.formatted_address || place.formatted_address || '';
          
          let finalAddress = '';
          if (name && address) {
            finalAddress = `${name}, ${address}`;
          } else if (name) {
            finalAddress = name;
          } else {
            finalAddress = address;
          }
          
          onChange(finalAddress);
          if (onPlaceSelected) onPlaceSelected(finalAddress, place);
        })
        .catch(() => {
          const finalAddress = place.formatted_address || displayText || '';
          onChange(finalAddress);
          if (onPlaceSelected) onPlaceSelected(finalAddress, place);
        });
    } else {
      let finalAddress = '';
      if (place.name && place.formatted_address) {
        finalAddress = `${place.name}, ${place.formatted_address}`;
      } else if (displayText && place.formatted_address && displayText !== place.formatted_address) {
        finalAddress = `${displayText}, ${place.formatted_address}`;
      } else {
        finalAddress = place.formatted_address || displayText || '';
      }
      
      onChange(finalAddress);
      if (onPlaceSelected) onPlaceSelected(finalAddress, place);
    }
  };

  // Initiates the process to get user's current location using browser geolocation API
  const getCurrentLocation = () => {
    if (isGettingLocation) {
      return;
    }

    if (navigator.permissions && navigator.permissions.query) {
      if (navigator.geolocation) {
        navigator.permissions
          .query({ name: "geolocation" })
          .then(getlatlang).catch(() => {
            setIsGettingLocation(false);
          });
      } else {
        alert("Sorry Not available!");
      }
    } else if (navigator.geolocation) {
      getlatlang();
    } else {
      toast.error("Geolocation is not supported by your browser. Please enter location manually.", {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  // Gets user's current coordinates and converts them to a formatted address using reverse geocoding
  const getlatlang = () => {
    setIsGettingLocation(true);
    
    navigator.geolocation.getCurrentPosition(function (position) {
      const coords = position.coords;
      
      // Calculates the distance between two coordinates using the Haversine formula (returns distance in meters)
      const calculateDistance = (lat1, lng1, lat2, lng2) => {
        const R = 6371000;
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLng = (lng2 - lng1) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                  Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                  Math.sin(dLng/2) * Math.sin(dLng/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        return R * c;
      };
      
      Geocode.setApiKey(process.env.REACT_APP_GOOGLE_LOCATION_API_KEY);
      Geocode.setLanguage("en");
      Geocode.setRegion("in");
      Geocode.setLocationType("ROOFTOP");

      Geocode.fromLatLng(coords.latitude, coords.longitude).then(
        (response) => {
          setIsGettingLocation(false);
          if (response.results && response.results.length > 0) {
            let mostPreciseResult = null;
            let minDistanceForPrecise = Infinity;
            
            response.results.forEach((result) => {
              const resultTypes = result.types || [];
              const isPreciseType = resultTypes.includes('street_address') || 
                                   resultTypes.includes('premise') ||
                                   resultTypes.includes('subpremise');
              
              if (isPreciseType) {
                const resultLat = result.geometry?.location?.lat;
                const resultLng = result.geometry?.location?.lng;
                
                if (resultLat && resultLng) {
                  const distance = calculateDistance(
                    coords.latitude, 
                    coords.longitude, 
                    resultLat, 
                    resultLng
                  );
                  
                  if (distance < minDistanceForPrecise) {
                    minDistanceForPrecise = distance;
                    mostPreciseResult = result;
                  }
                }
              }
            });
            
            if (mostPreciseResult) {
              const fullAddress = mostPreciseResult.formatted_address;
              onChange(fullAddress);
              if (onPlaceSelected) onPlaceSelected(fullAddress, mostPreciseResult);
            } else {
              let closestResult = response.results[0];
              let minDistance = Infinity;
              
              response.results.forEach((result) => {
                const resultLat = result.geometry?.location?.lat;
                const resultLng = result.geometry?.location?.lng;
                
                if (resultLat && resultLng) {
                  const distance = calculateDistance(
                    coords.latitude, 
                    coords.longitude, 
                    resultLat, 
                    resultLng
                  );
                  
                  if (distance < minDistance) {
                    minDistance = distance;
                    closestResult = result;
                  }
                }
              });
              
              const fullAddress = closestResult.formatted_address;
              onChange(fullAddress);
              if (onPlaceSelected) onPlaceSelected(fullAddress, closestResult);
            }
          }
        },
        () => {
          setIsGettingLocation(false);
          toast.error("Failed to get location. Please try again.", {
            position: "top-right",
            autoClose: 3000,
          });
        }
      );
    }, function (e) {
      setIsGettingLocation(false);
      let errorMessage = "Location access denied. Please enter location manually.";
      
      if (e.code === e.TIMEOUT) {
        errorMessage = "Location request timed out. Please try again or enter location manually.";
      } else if (e.code === e.POSITION_UNAVAILABLE) {
        errorMessage = "Location information unavailable. Please enter location manually.";
      } else if (e.code === e.PERMISSION_DENIED) {
        errorMessage = "Location access denied. Please enable location permissions or enter location manually.";
      }
      
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 4000,
      });
    }, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    });
  };

  return (
    <div className="form-group">
      <Autocomplete
        apiKey={process.env.REACT_APP_GOOGLE_LOCATION_API_KEY}
        onPlaceSelected={handlePlaceSelected}
        options={{
          componentRestrictions: { country: "in" },
          types: ['geocode', 'establishment'],
        }}
        type="text"
        className={className}
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        required={required}
      />
      {showCurrentLocationButton && (
        <label 
          className={`location location-button ${isGettingLocation ? 'loading' : ''}`}
          onClick={isGettingLocation ? undefined : getCurrentLocation}
        >
          <div className="location-icon-text">
            {isGettingLocation ? (
              <>
                <span className="location-spinner"></span>
                <span>Getting Location...</span>
              </>
            ) : (
              <>
                <img src="/assets/images/LocationIcon.svg" alt="location" className="location-icon" />
                <span>Use Current Location</span>
              </>
            )}
          </div>
        </label>
      )}
    </div>
  );
};

export default LocationAutocomplete;
